/**
 * StatCards - reusable row of metric cards with bottom-aligned numbers.
 *
 * Handles both simple (display-only) and interactive (clickable with active state) cards.
 */

interface StatCard {
  key: string
  label: string
  value: string | number
  /** When provided, card becomes clickable */
  onClick?: () => void
  /** Active highlight (left green border) */
  active?: boolean
}

interface StatCardsProps {
  cards: StatCard[]
}

export function StatCards({ cards }: StatCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mb-6">
      {cards.map((card) => {
        const isClickable = !!card.onClick
        return (
          <div
            key={card.key}
            onClick={card.onClick}
            className="flex flex-col justify-between"
            style={{
              background: 'var(--bg-subtle)',
              borderRadius: 8,
              padding: '16px 18px',
              ...(isClickable
                ? {
                    borderLeft: card.active ? '2px solid var(--green)' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease',
                    userSelect: 'none' as const,
                  }
                : {}),
            }}
            onMouseEnter={
              isClickable && !card.active
                ? (e) => (e.currentTarget.style.borderLeftColor = 'var(--green-border)')
                : undefined
            }
            onMouseLeave={
              isClickable && !card.active
                ? (e) => (e.currentTarget.style.borderLeftColor = 'transparent')
                : undefined
            }
          >
            <p
              style={{
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'var(--ink-3)',
                fontWeight: 400,
                marginBottom: 8,
              }}
            >
              {card.label}
            </p>
            <p
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 34,
                fontWeight: 300,
                color: 'var(--ink)',
                lineHeight: 1,
              }}
            >
              {card.value}
            </p>
          </div>
        )
      })}
    </div>
  )
}
